"use client"

import React, {useEffect} from "react"
import {usePathname, useSearchParams} from "next/navigation"
import Script from "next/script"
import {useRouter} from "next/router";
import {ParsedUrlQuery} from "querystring";
import {orderTransaction, pageview, transaction} from "./gtm"
import {
    DEFAULT_GTM_ID,
    PARAMETER_CAMPAIGN_ID,
    PARAMETER_CHANNEL_ID, PARAMETER_ODER_TRANSACTION_ID,
    PARAMETER_TRANSACTION_ID, SESSION_KEY_ORDER_TRANSACTION_DATA, SESSION_KEY_TRANSACTION_DATA
} from "../../pages/config/settings";
import {useSessionContext} from "../../context/SessionContext";
import {usePageContext} from "../../context/PageContext";
import {CartForTransaction} from "../../components/forms/F005OrderForm/formUtils";
import useOverlayService from "../../utility/consentManagement/useOverlayService";
import useHandleScripts from "../../utility/consentManagement/useHandleScripts";

interface Props {
    gtmId?: string,
    referrer?: string | URL,
}

const DIRECT_ACCESS = "direct";
const OTHER_LINKS = "referal";

function getGtmId(gtmId?: string): string {
    if (gtmId) {
        return gtmId;
    } else {
        return DEFAULT_GTM_ID;
    }
}

const getMatchingParam = (queryParams?: ParsedUrlQuery, paramSettings?: any) => {
    if (!queryParams || !paramSettings) return;
    for (let i = 0; i < Object.keys(queryParams).length; i++) {
        const paramValue = queryParams[Object.keys(queryParams)[i]];
        if (typeof paramValue !== "string") {
            break;
        }
        // get the mapped parameter value based on the current parameter
        let mappedParamValue = paramSettings?.[Object.keys(queryParams)[i]];

        // if the mapped parameter value is an object, attempt to get the nested value
        if (typeof mappedParamValue !== "string") {
            mappedParamValue = paramSettings?.[Object.keys(queryParams)[i]]?.[paramValue];
        }
        if (mappedParamValue) return mappedParamValue;
    }
}
const getDomain = (url: string | URL): string => {
    const parsedURL = new URL(url);
    return parsedURL.origin;
}


export default function Analytics(props: Props) {
    const session = useSessionContext()
    const GTM_ID = getGtmId(props.gtmId);
    const [transactionId, setTransactionId] = React.useState('init');
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const pageContext = usePageContext();
    const router = useRouter();

    const queryParams = router.query;
    const marketingSettings = pageContext.page?.marketingSettings;
    const channelDocRefererSettings = marketingSettings["channel-doc-referer"];
    const channelUrlParameterSettings = marketingSettings["channel-url-parameter"];
    const channelIdSettings = marketingSettings["channelIDs"];
    const referrerDomain = props.referrer ? getDomain(props.referrer) : undefined;

    const dataLayerScript = `window.dataLayer = window.dataLayer || []; function gtag() { dataLayer.push(arguments); }
    gtag("consent", "default", { ad_user_data: "denied", ad_personalization: "denied", ad_storage: "denied", analytics_storage: "denied", wait_for_update: 2000});
    gtag("set", "ads_data_redaction", true);`

    const gtmScript = `(function(w, d, s, l, i) { w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f); })(window, document, 'script', 'dataLayer', '${GTM_ID}');`

    useOverlayService();
    useHandleScripts();

    useEffect(() => {
        const campaignId = session.getFromSession(PARAMETER_CAMPAIGN_ID);
        let channelId = session.getFromSession(PARAMETER_CHANNEL_ID);

        /* if the channelId is not specified in the url query parameters, an attempt is made to determine the channelId via other
        channel parameters: (1) from other query parameters of the url, (2) from the referrer
         */
        if (!channelId) {
            let marketingParam;
            if (getMatchingParam(queryParams, channelUrlParameterSettings)) {
                marketingParam = getMatchingParam(queryParams, channelUrlParameterSettings);
            } else if (!referrerDomain) {
                marketingParam = DIRECT_ACCESS;
            } else {
                marketingParam = channelDocRefererSettings?.hasOwnProperty(referrerDomain) ? channelDocRefererSettings[referrerDomain] : OTHER_LINKS;
            }
            channelId = channelIdSettings?.[marketingParam];
        }

        if (channelId) {
            session.addToSession(PARAMETER_CHANNEL_ID, channelId);
        }

        let tId = session.getFromSession(PARAMETER_TRANSACTION_ID);
        let otId = session.getFromSession(PARAMETER_ODER_TRANSACTION_ID);
        // new transactionId is set by donation-REST-action
        if (tId && tId !== '' && tId != transactionId) {
            setTransactionId(tId);
            console.log("Log-transaction: %s", tId)
            const transactionData = session.getJsonFromSession(SESSION_KEY_TRANSACTION_DATA);
            transaction(tId, campaignId, channelId, transactionData);
            // remove transactionId after event was sent
            session.removeFromSession(PARAMETER_TRANSACTION_ID);
        } else if (otId && otId !== '' && otId != transactionId) {
            setTransactionId(otId);
            const orderTransactionData = session.getJsonFromSession(SESSION_KEY_ORDER_TRANSACTION_DATA) as CartForTransaction|null;
            console.log("Log-order-transaction: %s", otId);
            orderTransaction(otId, orderTransactionData);
            session.removeFromSession(PARAMETER_ODER_TRANSACTION_ID);
            session.removeFromSession(SESSION_KEY_ORDER_TRANSACTION_DATA);
        } else {
            if (pathname) {
                pageview(pathname, campaignId, channelId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, searchParams])

    return (
        <>
            <noscript>
                <iframe
                    src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
                    height="0"
                    width="0"
                    style={{display: "none", visibility: "hidden"}}
                />
            </noscript>
            <Script
                id="datalayer-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: dataLayerScript
                }}
            />
            <Script
                id="gtm-script"
                strategy="afterInteractive"
                type="text/plain"
                data-usercentrics="Google Tag Manager"
                dangerouslySetInnerHTML={{
                    __html: gtmScript
                }}
            />
        </>
    )
}